import { Injectable } from '@angular/core';
import {
  LAMPORTS_PER_SOL,
  PublicKey,
  SignatureStatus,
  Transaction,
  TransactionExpiredBlockheightExceededError,
  TransactionSignature
} from '@solana/web3.js';
import {
  BehaviorSubject,
  combineLatest,
  firstValueFrom,
  from,
  Observable,
  of,
  ReplaySubject,
  startWith,
  SubscriptionLike,
  switchMap,
  tap
} from 'rxjs';
import { delay } from 'rxjs/operators';
import {
  createUnwrapSOLTransaction,
  createWrapSOLTransaction,
  neonBalanceProgramAddress,
  neonWrapper2Abi,
  solanaSOLTransferTransaction,
  SPLToken,
  wrappedNeonTransaction
} from '@neonevm/token-transfer-core';
import { GoogleAnalyticsService } from 'ngx-google-analytics';
import { Big } from 'big.js';
import { Interface, parseUnits, TransactionRequest, TransactionResponseParams } from 'ethers';
import {
  decodeTransaction,
  itemUnsubscribe,
  NEON,
  priorityFeeLamports,
  SOL,
  timeout,
  TRANSACTION_FEE,
  W_NEON,
  W_SOL
} from '../../utils';
import {
  Address,
  Amount,
  PendingStatus,
  TransferDirection,
  TransferTokenFormData,
  TransferTokenLog,
  TransferTransaction,
  TransferTransactionConfirmation
} from '../../models';
import { HttpRpcClient } from '../../app/services';
import { environment } from '../../environments/environment';
import { ProxyStatusService } from './proxy-status.service';
import { SolanaWalletService } from './solana-wallet.service';
import { TransferTransactionService } from './transfer-transaction.service';
import { WalletConnectService } from './wallet-connect.service';
import { NeonChainService } from './neon-chain.service';
import { PriorityFeeService } from './priority-fee.service';
import { NeonTransferFeeService } from './neon-transfer-fee.service';
import { PythService } from './pyth.service';
import { ApiService } from '../../api/api.service';
import { decode } from 'bs58';

@Injectable({ providedIn: 'root' })
export class TokenTransferService {
  formData$: ReplaySubject<TransferTokenFormData> = new ReplaySubject<TransferTokenFormData>(0);
  newAccountFee$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  pendingStatus$: BehaviorSubject<PendingStatus> = new BehaviorSubject<PendingStatus>(PendingStatus.default);
  transferData: TransferTokenFormData;
  private _transferLog$: ReplaySubject<TransferTokenLog> = new ReplaySubject<TransferTokenLog>(0);
  private _transferLog: TransferTokenLog = {};
  private subs: SubscriptionLike[] = [];
  private publicClient: any;

  get isSameNetwork(): boolean {
    return this.neon.chainId === this.proxy.chainId;
  }

  get isSolNetwork(): boolean {
    return this.proxy.gasToken$.value.tokenName === SOL;
  }

  get transferLog$(): Observable<TransferTokenLog> {
    return this._transferLog$.pipe(startWith(this._transferLog));
  }

  getGasPrice = async (transaction: TransactionRequest): Promise<{ gas: number, gasPrice: number }> => {
    const { from, to, data, value } = transaction;
    try {
      const gas = await this.neon.provider!.estimateGas({ from, to, value, data });
      const feeGas = await this.neon.provider!.getFeeData();
      // @deprecated
      // const gasPrice = await this.neon.provider.getGasPrice();
      return { gas: Number(gas), gasPrice: Number(feeGas.gasPrice) };
    } catch (e: any) {
      console.log(e?.message);
      return { gas: 25000, gasPrice: 235906041209 };
    }
  };

  _getGasPrice = async (transaction: TransactionRequest, url: string): Promise<{ gas: number, gasPrice: number }> => {
    const { from, to, data, value } = transaction;
    const gasPrice = await firstValueFrom(this.apiRpc.rpc(`eth_gasPrice`, [], url));
    const gas = await firstValueFrom(this.apiRpc.rpc(`eth_estimateGas`, [{ from, to, data, value }], url));
    return ({ gas: Number(gas), gasPrice: Number(gasPrice) });
  };

  transfer(data: TransferTokenFormData): Observable<TransferTransaction> {
    this.transferData = data;
    this.transferLog('data', data);
    this.ga.event(`token_transfer_${data?.token.token.name || ''}`, `${data?.direction}_transfer`, undefined, undefined, undefined, {
      neon_account: this.neon.address$.value,
      solana_account: this.solana.publicKey.toString(),
      direction: data?.direction || '',
      amount: data?.amount || 0,
      token: data?.token.token.name || '',
      priority_fee: data.priorityFee
    });
    return from(this.transferMethod(data));
  }

  async transferMethod(data: TransferTokenFormData): Promise<TransferTransaction> {
    const { direction, amount, rewardFrom, token: { token } } = data;
    const priorityFee = this.fee.selected$.value.amount; // micro-lamports
    const neonDirection = direction === TransferDirection.neon;
    if (environment.depositsClosed && !neonDirection) {
      throw new Error('Deposits to Neon EVM are closed. Only withdrawals to Solana are available.');
    }
    switch (token.symbol) {
      case NEON: {
        if (neonDirection) {
          return this.transferNEONToSolana(amount, token);
        } else if (rewardFrom === 'neon') {
          const units = this.fee.data.priorityFee?.units; // micro-lamports
          const pFee = priorityFeeLamports(priorityFee, units).add(TRANSACTION_FEE).round();
          const solPerNeon = this.pyth.solPerNeon;
          const neon = this.pyth.neonPrice;
          const rewardAmountAndFee = pFee.times(solPerNeon).times(neon).round();
          const sendAmount = new Big(amount.toString()).times(LAMPORTS_PER_SOL).minus(rewardAmountAndFee).div(LAMPORTS_PER_SOL);
          return this.transferNEONWithNeonFeeToNeon(sendAmount.toString(), rewardAmountAndFee.toString(), token, priorityFee);
        }
        return this.transferNEONToNeon(amount, token, priorityFee);
      }
      case W_NEON:
        return this.transferWNEONToSolana(amount, token);
      // case SOL:
      // if (this.isSolNetwork) {
      //   if (neonDirection) {
      //     return this.transferSOLToSolana(amount, token);
      //   }
      //   return this.transferSOLToNeon(amount, token);
      // }
      // return this.transferWSOLToNeon(amount, token, priorityFee);
      case SOL:
      case W_SOL:
        return (neonDirection ? this.transferWSOLToSolana : this.transferSOLToNeon)(amount, token, priorityFee);
    }
    return (neonDirection ? this.transferERC20ToSolana : this.transferERC20ToNeon)(amount, token, priorityFee);
  }

  //Solana -> Neon
  transferNEONToNeonTransaction = (amount: Amount, token: SPLToken): Promise<Transaction> => {
    this.transferLog('method', 'transferNEONToNeonTransaction');
    const method = async (a: Amount) => {
      const { transaction } = await firstValueFrom(this.api.solanaTransferNeonToken(this.solana.publicKey, this.neon.address, a));
      return Transaction.from(decode(transaction));
    };
    return this.transaction.solanaTransaction(amount, token, method);
  };

  //Solana -> Neon
  transferNEONToNeonWithNeonFeeTransaction = (amount: Amount, token: SPLToken, serviceWallet: PublicKey, rewardAmount: Amount, priorityFee: number): Promise<Transaction> => {
    const method = async (a: Amount, t: SPLToken) => {
      const { transaction } = await firstValueFrom(this.api.solanaTransferNeonTokenWithNeonFee(this.solana.publicKey, serviceWallet, this.neon.address, a, rewardAmount));
      let trx = Transaction.from(decode(transaction));
      trx = await this.fee.transactionPriorityFee(trx, priorityFee);
      return trx;
    };
    return this.transaction.solanaTransaction(amount, token, method);
  };

  transferNEONToSolanaTransaction = (amount: Amount, token: SPLToken, contract: `0x${string}`): Promise<TransactionRequest> => {
    this.transferLog('method', 'transferNEONToSolanaTransaction');
    const method = async (a: Amount) => {
      const { neonTransaction } = await firstValueFrom(this.api.neonTransferNeonToken(this.neon.address, this.solana.publicKey, a));
      return decodeTransaction(neonTransaction);
    };
    return this.transaction.neonTransaction(amount, token, method);
  };

  //Solana -> Neon
  transferSOLToNeonTransaction = (amount: Amount, token: SPLToken): Promise<Transaction> => {
    this.transferLog('method', 'transferSOLToNeonTransaction');
    const method = async (a: Amount, t: SPLToken) => {
      return await solanaSOLTransferTransaction({
        connection: this.solana.connection,
        solanaWallet: this.solana.publicKey,
        neonWallet: this.neon.address,
        neonEvmProgram: this.proxy.programId,
        neonTokenMint: this.proxy.tokenMint,
        splToken: t,
        amount: a,
        chainId: this.proxy.chainId
      });
    };
    return this.transaction.solanaTransaction(amount, token, method);
  };

  //Solana -> Neon
  transferERC20ToNeonTransaction = (amount: Amount, token: SPLToken): Promise<Transaction> => {
    this.transferLog('method', 'transferERC20ToNeonTransaction');
    const method = async (a: Amount, t: SPLToken) => {
      const { transaction } = await firstValueFrom(this.api.solanaTransferSplToken(this.solana.publicKey, this.neon.address, new PublicKey(t.address_spl), a));
      return Transaction.from(decode(transaction));
    };
    return this.transaction.solanaTransaction(amount, token, method);
  };

  //Solana -> Neon
  transferWSOLToNeonTransaction = (amount: Amount, token: SPLToken): Promise<Transaction> => {
    this.transferLog('method', 'transferWSOLToNeonTransaction');
    const method = async (a: Amount, t: SPLToken) => {
      const { transaction } = await firstValueFrom(this.api.solanaTransferWSolToken(this.solana.publicKey, this.neon.address, new PublicKey(t.address_spl), a));
      return Transaction.from(decode(transaction));
    };
    return this.transaction.solanaTransaction(amount, token, method);
  };

  //Solana -> Neon
  transferERC20ToSolanaSolanaTransaction = async (amount: Amount, token: SPLToken): Promise<Transaction> => {
    const method = async (a: Amount, t: SPLToken) => {
      const { solanaTransaction } = await firstValueFrom(this.api.neonTransferSplToken(this.neon.address, this.solana.publicKey, new PublicKey(t.address_spl), a));
      if (solanaTransaction) {
        return Transaction.from(decode(solanaTransaction));
      }
      return new Transaction();
    };
    return this.transaction.solanaTransaction(amount, token, method);
  };

  transferERC20ToSolanaNeonTransaction = async (amount: Amount, token: SPLToken): Promise<TransactionRequest> => {
    this.transferLog('method', 'transferERC20ToSolanaNeonTransaction');
    const method = async (a: Amount, t: SPLToken) => {
      const { neonTransaction } = await firstValueFrom(this.api.neonTransferSplToken(this.neon.address, this.solana.publicKey, new PublicKey(t.address_spl), a));
      return decodeTransaction(neonTransaction);
    };
    return this.transaction.neonTransaction(amount, token, method);
  };

  unwrapWNEONInNeonTransaction = async (amount: Amount, token: SPLToken): Promise<TransactionRequest> => {
    this.transferLog('method', 'unwrapWNEONInNeonTransaction');
    const method = async (a: Amount, t: SPLToken) => {
      const tokenContract = new Interface(neonWrapper2Abi);
      const fullAmount = parseUnits(a.toString(), token.decimals);
      const data = tokenContract.encodeFunctionData('withdraw', [fullAmount]);
      return wrappedNeonTransaction<TransactionResponseParams>(this.neon.address, t.address, data);
    };
    return this.transaction.wNeonTransaction(amount, token, method);
  };

  wrapSOLInSolanaTransaction = async (amount: Amount, token: SPLToken): Promise<Transaction> => {
    this.transferLog('method', 'wrapSOLInSolanaTransaction');
    const method = async (a: Amount, t: SPLToken) => {
      return createWrapSOLTransaction({
        connection: this.solana.connection,
        solanaWallet: this.solana.publicKey,
        amount: a,
        splToken: t
      });
    };
    return this.transaction.solanaTransaction(amount, token, method);
  };

  unwrapSOLInSolanaTransaction = async (amount: Amount, token: SPLToken): Promise<Transaction> => {
    this.transferLog('method', 'unwrapSOLInSolanaTransaction');
    const method = async (a: Amount, t: SPLToken) => {
      return createUnwrapSOLTransaction(this.solana.connection, this.solana.publicKey, t);
    };
    return this.transaction.solanaTransaction(amount, token, method);
  };

  // for transfer NEON (Solana -> Neon) with Neon fee
  transferNEONWithNeonFeeToNeon = async (amount: Amount, rewardAmount: Amount, token: SPLToken, priorityFee: number): Promise<TransferTransaction> => {
    return this.tryTransfer(async () => {
      const serviceWallet = this.neonFee.serviceWallet$.value!;
      const transaction = await this.transferNEONToNeonWithNeonFeeTransaction(amount, token, serviceWallet, rewardAmount, priorityFee);
      transaction.feePayer = serviceWallet;
      this.pendingStatus$.next(PendingStatus.signature);
      const signedTransaction = await this.solana.provider.signTransaction(transaction);
      this.pendingStatus$.next(PendingStatus.signed);
      const [, walletSign] = signedTransaction.signatures;
      const serializedTx = signedTransaction.serialize({ verifySignatures: false });
      const data = Buffer.from(serializedTx).toString('hex');
      const signature = Buffer.from(walletSign.signature!).toString('hex');
      const response = await firstValueFrom(this.neonFee.sendTransaction({ data, signature }));
      const solana = { transaction: signedTransaction, signature: response?.signature };
      this.transaction.solanaClean();
      this.transferLog('transaction', { solana });
      return { solana };
    });
  };

  // for transfer NEON (Solana -> Neon) with Solana fee
  transferNEONToNeon = async (amount: Amount, token: SPLToken, priorityFee: number): Promise<TransferTransaction> => {
    return this.tryTransfer(async () => {
      let transaction = await this.transferNEONToNeonTransaction(amount, token);
      transaction = await this.fee.transactionPriorityFee(transaction, priorityFee);
      const { signature, signedTransaction } = await this.solanaSendTransaction(transaction);
      const solana = { transaction: signedTransaction, signature };
      this.transaction.solanaClean();
      this.transferLog('transaction', { solana });
      return { solana };
    });
  };

  transferNEONToSolana = async (amount: Amount, token: SPLToken, skipStart = false): Promise<TransferTransaction> => {
    return this.tryTransfer(async () => {
      const transaction = await this.transferNEONToSolanaTransaction(amount, token, environment.neon.token_contract as Address);
      this.pendingStatus$.next(PendingStatus.signature);
      const signedTransaction = await this.neon.signTransaction(transaction);
      this.pendingStatus$.next(PendingStatus.signed);
      const neon = { transaction, signature: signedTransaction };
      this.transferLog('transaction', { neon });
      await this.neon.getTransactionReceipt(signedTransaction);
      this.transaction.neonClean();
      return { neon };
    }, skipStart);
  };

  transferSOLToSolana = async (amount: Amount, token: SPLToken): Promise<TransferTransaction> => {
    return this.tryTransfer(async () => {
      const transaction = await this.transferNEONToSolanaTransaction(amount, token, environment.neon.token_contract_sol as Address);
      this.pendingStatus$.next(PendingStatus.signature);
      const signedTransaction = await this.neon.signTransaction(transaction);
      this.pendingStatus$.next(PendingStatus.signed);
      const neon = { transaction, signature: signedTransaction };
      this.transferLog('transaction', { neon });
      await this.neon.getTransactionReceipt(signedTransaction);
      this.transaction.neonClean();
      return { neon };
    });
  };

  transferSOLToNeon = async (amount: Amount, token: SPLToken, priorityFee: number): Promise<TransferTransaction> => {
    return this.tryTransfer(async () => {
      let transaction = await this.transferWSOLToNeonTransaction(amount, token);
      transaction = await this.fee.transactionPriorityFee(transaction, priorityFee);
      const { signature, signedTransaction } = await this.solanaSendTransaction(transaction);
      const solana = { transaction: signedTransaction, signature };
      this.transferLog('transaction', { solana });
      this.transaction.neonClean();
      return { solana };
    });
  };

  transferERC20ToNeon = async (amount: Amount, token: SPLToken, priorityFee: number): Promise<TransferTransaction> => {
    return this.tryTransfer(async () => {
      let transaction = await this.transferERC20ToNeonTransaction(amount, token);
      transaction = await this.fee.transactionPriorityFee(transaction, priorityFee);
      const { signature, signedTransaction } = await this.solanaSendTransaction(transaction);
      const solana = { transaction: signedTransaction, signature };
      this.transferLog('transaction', { solana });
      this.transaction.solanaClean();
      return { solana };
    });
  };

  transferERC20ToSolana = async (amount: Amount, token: SPLToken): Promise<TransferTransaction> => {
    return this.tryTransfer(async () => {
      let solana;
      const solanaTransaction = await this.transferERC20ToSolanaSolanaTransaction(amount, token);
      if (solanaTransaction.instructions.length > 0) {
        const { signature, signedTransaction } = await this.solanaSendTransaction(solanaTransaction);
        solana = { transaction: signedTransaction, signature };
        await this.confirmTransaction({ solana });
      }
      const neonTransaction = await this.transferERC20ToSolanaNeonTransaction(amount, token);
      this.pendingStatus$.next(PendingStatus.signature);
      const neonSignedTransaction = await this.neon.signTransaction(neonTransaction);
      this.pendingStatus$.next(PendingStatus.signed);
      const neon = { transaction: neonTransaction, signature: neonSignedTransaction };
      this.transferLog('transaction', { solana, neon });
      await this.neon.getTransactionReceipt(neonSignedTransaction);
      this.transaction.clean();
      return { neon, solana };
    });
  };

  transferWNEONToSolana = async (amount: Amount, token: SPLToken): Promise<TransferTransaction> => {
    await this.unwrapWNEONInNeon(amount, token);
    return this.transferNEONToSolana(amount, token, true);
  };

  transferWSOLToNeon = async (amount: Amount, token: SPLToken, priorityFee: number): Promise<TransferTransaction> => {
    return this.tryTransfer(async () => {
      let transaction = await this.transferWSOLToNeonTransaction(amount, token);
      transaction = await this.fee.transactionPriorityFee(transaction, priorityFee);
      this.pendingStatus$.next(PendingStatus.signature);
      const signedTransaction = await this.solana.provider.signTransaction(transaction);
      this.pendingStatus$.next(PendingStatus.signed);
      const signature = await this.solana.connection.sendRawTransaction(signedTransaction.serialize(), { skipPreflight: false });
      const solana = { transaction, signature };
      this.transferLog('transaction', { solana });
      this.transaction.solanaClean();
      return { solana };
    });
  };

  transferWSOLToSolana = async (amount: Amount, token: SPLToken): Promise<TransferTransaction> => {
    await this.transferERC20ToSolana(amount, token);
    return this.unwrapSOLInSolana(amount, token);
  };

  unwrapWNEONInNeon = async (amount: Amount, token: SPLToken): Promise<TransferTransaction> => {
    return this.tryTransfer(async () => {
      const transaction = await this.unwrapWNEONInNeonTransaction(amount, token);
      this.pendingStatus$.next(PendingStatus.signature);
      const signedTransaction = await this.neon.signTransaction(transaction);
      const neon = { transaction, signature: signedTransaction };
      await this.neon.getTransactionReceipt(signedTransaction);
      this.transaction.clean();
      return { neon };
    });
  };

  wrapSOLInSolana = (amount: Amount, token: SPLToken): Promise<TransferTransaction> => {
    return this.tryTransfer(async () => {
      const transaction = await this.wrapSOLInSolanaTransaction(amount, token);
      this.pendingStatus$.next(PendingStatus.signature);
      const signedTransaction = await this.solana.provider.signTransaction(transaction);
      const signature = await this.solana.connection.sendRawTransaction(signedTransaction.serialize(), { skipPreflight: false });
      const solana = { transaction: signedTransaction, signature };
      this.transaction.solanaClean();
      return { solana };
    });
  };

  unwrapSOLInSolana = async (amount: Amount, token: SPLToken): Promise<TransferTransaction> => {
    const transaction = await this.unwrapSOLInSolanaTransaction(amount, token);
    const { signature, signedTransaction } = await this.solanaSendTransaction(transaction);
    const solana = { transaction: signedTransaction, signature };
    this.transaction.solanaClean();
    return { solana };
  };

  solanaSendTransaction = async (transaction: Transaction): Promise<{
    signature: TransactionSignature;
    signedTransaction: Transaction;
  }> => {
    const { lastValidBlockHeight, blockhash } = await firstValueFrom(this.api.solanaBlockHash());
    transaction.recentBlockhash = blockhash;
    transaction.lastValidBlockHeight = lastValidBlockHeight;
    this.pendingStatus$.next(PendingStatus.signature);
    const signedTransaction = await this.solana.provider.signTransaction(transaction);
    this.pendingStatus$.next(PendingStatus.signed);
    const signature = await this.solana.connection.sendRawTransaction(signedTransaction.serialize(), { skipPreflight: false });
    return { signature, signedTransaction };
  };

  confirmTransaction = (data: TransferTransaction): Promise<TransferTransactionConfirmation> => {
    return new Promise(async (resolve, reject) => {
      const result: TransferTransactionConfirmation = {};
      try {
        if (data.solana?.signature) {
          const { lastValidBlockHeight, blockhash } = await firstValueFrom(this.api.solanaBlockHash());
          const response = await this.solana.connection.confirmTransaction({
            blockhash,
            lastValidBlockHeight,
            signature: data.solana.signature
          });
          result.solana = response.value;
        }
        resolve(result);
      } catch (e: any) {
        /*
         * the TransactionExpiredBlockheightExceededError may occur even when a transaction ultimately succeeds on-chain.
         * This happens because the @solana/web3.js library relies on a timeout mechanism to wait for transaction confirmation.
         * If the timeout expires before the library receives confirmation, it assumes the transaction has failed and throws the error.
         * Using getTransaction to check whether the transaction landed on-chain - returns null if transaction is not found or not confirmed.
         */
        if (e instanceof TransactionExpiredBlockheightExceededError) {
          const transactionConfirmationStatus = await this.checkTransactionConfirmationStatus(data.solana?.signature);
          if (transactionConfirmationStatus) {
            resolve({ ...result, solana: transactionConfirmationStatus });
          } else {
            reject(e);
          }
        } else {
          reject(e);
        }
      }
    });
  };

  transferLog(key: keyof TransferTokenLog, size: any): void {
    this._transferLog[key] = size;
    this._transferLog$.next(this._transferLog);
  }

  clearTransferLog(): void {
    this._transferLog = {};
    this._transferLog$.next(this._transferLog);
    this.pendingStatus$.next(PendingStatus.started);
  }

  provideNewAccountFee(address: string, publicKey: PublicKey): Observable<any> {
    if (address && publicKey) {
      const [neonBalanceAccount] = neonBalanceProgramAddress(address, this.proxy.programId, this.proxy.chainId);
      // const [neonBalanceAccount] = neonBalanceProgramAddressV2(address, publicKey, this.proxy.programId, this.proxy.chainId);
      return from(this.solana.connection.getAccountInfo(neonBalanceAccount)).pipe(tap(account => {
        this.newAccountFee$.next(!account);
      }));
    }
    return of(null).pipe(delay(100));
  }

  airdropClose(): void {
    this.newAccountFee$.next(false);
  }

  init(): void {
    this.subs.push(combineLatest([this.proxy.proxyStatus$, this.neon.address$, this.solana.publicKey$]).pipe(
      tap(([, neonWalletAddress, solanaWalletAddress]) => {
        if (!!neonWalletAddress && !!solanaWalletAddress) {
          this.publicClient = this.neon.publicClient;
        }
      }),
      switchMap(([_, n, s]) => this.provideNewAccountFee(n, s))).subscribe());
    this.pendingStatus$.next(PendingStatus.confirmed);
  }

  destroy(): void {
    itemUnsubscribe(this.subs);
    this.pendingStatus$.complete();
  }

  private tryTransfer = (fn: () => any, skipStart = false): Promise<TransferTransaction> => {
    return new Promise(async (resolve, reject) => {
      if (!skipStart) {
        this.pendingStatus$.next(PendingStatus.started);
        await timeout(1000);
      }
      try {
        if (typeof fn === 'function') {
          if (!this.isSameNetwork) {
            await firstValueFrom(this.chain.chainSwitch());
            console.time('Chain switch emmit');
            await timeout(4e3);
            console.time('Timeout emmit');
          }
          const result = await fn();
          console.time('Result emit');
          resolve(result);
        }
      } catch (e) {
        this.transferLog('error', e as Error);
        this.transaction.clean();
        reject(e);
      }
    });
  };

  checkTransactionConfirmationStatus = async (signature: string = '', maxRetries = 30, interval = 2000): Promise<SignatureStatus | null> => {
    console.log(`Checking status for transaction: ${signature}`);
    if (!signature) return null;

    for (let i = 0; i < maxRetries; i++) {
      try {
        // Fetch the status of the transaction
        const statusResponse = await this.solana.connection.getSignatureStatuses([signature]);
        const [status] = statusResponse.value;
        if (status) {
          if (status.confirmationStatus === 'finalized' || status.confirmationStatus === 'confirmed') {
            console.log(`Transaction ${signature} is confirmed and finalized.`);
            return status;
          }
        }
      } catch (error) {
        console.error(`Error checking transaction status: ${error}`);
      }
      await new Promise(resolve => setTimeout(resolve, interval));
    }

    console.log(`Transaction ${signature} status polling timed out.`);
    return null;
  };

  constructor(public solana: SolanaWalletService, public neon: WalletConnectService, public proxy: ProxyStatusService,
              private neonFee: NeonTransferFeeService, private fee: PriorityFeeService, private ga: GoogleAnalyticsService,
              private chain: NeonChainService, private transaction: TransferTransactionService, private apiRpc: HttpRpcClient,
              private pyth: PythService, private api: ApiService) {
  }
}
